<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PublicVolunteerSlot } from '@/types/volunteer'

const props = defineProps<{
  slots: PublicVolunteerSlot[]
  modelValue: number[]
  fullSlotIds?: number[]
}>()

const emit = defineEmits<{
  'update:modelValue': [ids: number[]]
}>()

const { t } = useI18n()

/** Strip HH:MM:SS → HH:MM */
function formatTime(time: string): string {
  return time.length > 5 ? time.slice(0, 5) : time
}

/** Group slots by date */
const slotsByDate = computed(() => {
  const groups = new Map<string, PublicVolunteerSlot[]>()
  for (const slot of props.slots) {
    const existing = groups.get(slot.slot_date)
    if (existing) {
      existing.push(slot)
    } else {
      groups.set(slot.slot_date, [slot])
    }
  }
  return groups
})

function isSelected(id: number): boolean {
  return props.modelValue.includes(id)
}

function isFull(slot: PublicVolunteerSlot): boolean {
  return slot.confirmed_count >= slot.capacity
}

function isConflicted(id: number): boolean {
  return (props.fullSlotIds ?? []).includes(id)
}

function toggleSlot(slot: PublicVolunteerSlot): void {
  if (isFull(slot)) return
  const current = props.modelValue
  if (current.includes(slot.id)) {
    emit('update:modelValue', current.filter((id) => id !== slot.id))
  } else {
    emit('update:modelValue', [...current, slot.id])
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
</script>

<template>
  <div class="space-y-4">
    <!-- Conflict banner -->
    <div
      v-if="(fullSlotIds ?? []).length > 0"
      class="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800"
    >
      {{ t('volunteer.wizard.step2.slot_clash_banner') }}
    </div>

    <div
      v-for="[date, daySlots] in slotsByDate"
      :key="date"
      class="rounded-xl bg-white border border-slate-200 overflow-hidden"
    >
      <!-- Date heading -->
      <div class="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <p class="text-sm font-semibold text-slate-700">{{ formatDate(date) }}</p>
      </div>

      <!-- Slot chips -->
      <div class="p-3 flex flex-wrap gap-2">
        <button
          v-for="slot in daySlots"
          :key="slot.id"
          type="button"
          :aria-pressed="isSelected(slot.id)"
          :aria-disabled="isFull(slot)"
          :disabled="isFull(slot)"
          class="inline-flex flex-col items-center justify-center min-w-[76px] px-3 py-2 rounded-lg border text-xs font-medium transition-colors"
          :class="{
            'bg-sky-600 border-sky-600 text-white': isSelected(slot.id) && !isConflicted(slot.id),
            'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed': isFull(slot),
            'border-amber-400 ring-2 ring-amber-200 bg-amber-50 text-amber-800': isConflicted(slot.id),
            'bg-white border-slate-200 text-slate-700 hover:border-sky-400 hover:bg-sky-50': !isSelected(slot.id) && !isFull(slot) && !isConflicted(slot.id),
          }"
          @click="toggleSlot(slot)"
        >
          <span class="font-semibold">{{ formatTime(slot.start_time) }}</span>
          <span class="text-[10px] opacity-75">{{ formatTime(slot.end_time) }}</span>
          <span v-if="isSelected(slot.id) && !isFull(slot)" class="mt-0.5 text-[10px]">&#10003;</span>
          <span v-if="isFull(slot)" class="mt-0.5 text-[10px]">{{ t('volunteer.wizard.step2.slot_full') }}</span>
        </button>
      </div>

      <!-- Taken / open summary -->
      <div class="px-4 pb-3">
        <p aria-live="polite" class="text-xs text-slate-500">
          {{
            t('volunteer.wizard.step2.spots_taken_open', {
              taken: daySlots.reduce((sum, s) => sum + s.confirmed_count, 0),
              open: daySlots.reduce((sum, s) => sum + Math.max(0, s.capacity - s.confirmed_count), 0),
            })
          }}
        </p>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="slotsByDate.size === 0" class="text-center py-8 text-sm text-slate-500">
      {{ t('volunteer.organizer.settings.slots.empty') }}
    </div>
  </div>
</template>
